import React from "react";
import logotype90years from "../../assets/images/footer-logo-90-years.png";
import phoneIcon from "../../assets/images/phone.svg";
import useTranslations from "../../hooks/useTranslations";
import { SiteSettings } from "../../models/shared/SiteSettings";
import { isNullOrWhiteSpace } from "../../shared/string";
import { formatPhoneNumberLink } from "../../utilities/links";
import Button, { ButtonSize } from "../Buttons/Button";
import { MarketSelectorButton } from "../MarketSelectorButton";
import SanityImage from "../SanityImage";
import SanityLink, { checkLinkExists, renderLinkTitle } from "../SanityLink";
import { FooterAside } from "./FooterAside";

interface Props {
  siteSettings: SiteSettings;
}

export const MarketFooter = (props: Props) => {
  const siteSettings = props.siteSettings;
  const { footer, siteContact } = siteSettings || {};
  const {
    contactCta,
    hideLogo,
    hidePhoneNumber,
    marketButtonTitle,
    links,
    illustration,
    legalText
  } = footer || {};

  const translations = useTranslations();

  return (
    <footer className="flex flex-col items-center bg-footer-bg text-footer-text-color md:relative md:slice-top md:z-1">
      <div className="flex flex-col md:flex-row flex-wrap justify-center items-center flex-1 w-full max-w-screen-xl px-4 py-8 mx-auto md:px-8 md:py-16 lg:px-0">
        {!hideLogo && (
          <div className="flex w-1/3 sm:w-1/6 justify-center lg:w-1/4 md:justify-center lg:justify-end p-8 sm:p-4">
            <img
              src={logotype90years}
              alt={translations.anniversaryImageAlt || "Anticimex 90 years of innovation"}
              className="md:max-h-[12.5rem]"
            />
          </div>
        )}
        {!!illustration && (
          <div className="flex justify-center order-3 mb-4 md:order-none md:justify-start md:w-1/2 md:pl-20 md:mb-0">
            <SanityImage
              className="w-10/12 h-auto md:w-auto"
              image={illustration}
              loading="lazy"
              width={320}
              height={260}
            />
          </div>
        )}

        <div className="flex flex-wrap flex-col order-2 md:order-none md:flex-row justify-center items-center w-full md:justify-around lg:justify-center mb-4 md:mb-10">
          {checkLinkExists(contactCta) && !isNullOrWhiteSpace(contactCta.title) && (
            <div className="lg:px-4 py-4 lg:py-6">
              <Button
                color="primary-light"
                size={ButtonSize.SmallWide}
                className="inline-block"
                {...contactCta}
                type="link"
              >
                {contactCta.title}
              </Button>
            </div>
          )}
          {!!siteContact?.phoneNumber && !hidePhoneNumber && (
            <div className="lg:px-4 py-4 lg:py-6">
              <a
                href={formatPhoneNumberLink(siteContact.phoneNumber)}
                className="flex items-center mx-4 font-bold text-2xl md:text-3xl text-footer-text-color no-underline"
              >
                <svg width={32} height={32} className="fill-current mr-3">
                  <use xlinkHref={phoneIcon.url} />
                </svg>{" "}
                <span>{siteContact.phoneNumber}</span>
              </a>
            </div>
          )}
          {!!marketButtonTitle && (
            <div className="lg:px-4 py-4 lg:py-6">
              <MarketSelectorButton title={marketButtonTitle} />
            </div>
          )}
        </div>
        {!!links?.length && (
          <div className="flex justify-center order-last md:order-none">
            <ul className="flex flex-col md:flex-row">
              {links.map((link, index) => (
                <li key={index} className="mb-4 md:mx-5">
                  <SanityLink
                    {...link}
                    className="text-xl lg:text-2xl text-footer-text-color underline hover:text-footer-link-hover"
                  >
                    {renderLinkTitle(link)}
                  </SanityLink>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {!isNullOrWhiteSpace(legalText) && (
        <div className="max-w-3/4 pt-10 pb-14 text-center">
          <p className="text-neutral-75">{siteSettings.footer.legalText}</p>
        </div>
      )}
      <FooterAside siteSettings={siteSettings} />
    </footer>
  );
};

